import { ContentOptimizationStatus } from '@edgebox/sync-core-data-definitions';
import { ClientContentOptimizationEntity } from '@edgebox/sync-core-rest-client';

/** The title of the notice for a draft the site wrote while it awaits the review. */
export const SITE_DRAFT_REVIEW_TITLE_COPY = 'Draft ready for review';

/**
 * The notice's body on the page's own Optimize screen in the site, where the site's own step to publish the draft
 * stands below it for a person who may take it.
 */
export const SITE_DRAFT_REVIEW_BODY_IN_SITE_COPY =
  "The suggested changes are saved as a draft of this page. Review and publish the draft to make them live. We'll check the page again after it's published.";

/** The notice's body on the page detail in the App, which cannot publish the draft: the site does. */
export const SITE_DRAFT_REVIEW_BODY_IN_APP_COPY =
  "The suggested changes are saved as a draft on your site. Publish it there to make them live. We'll check the page again after it's published.";

/** What the screen says once the site reported that it published a draft it wrote. */
export const SITE_DRAFT_LIVE_COPY = 'Live on your site.';

/**
 * The statuses of a draft that awaits the person's review. Sync Core's own generator leaves a draft
 * that passed its review at system review, and a draft the site wrote is recorded at user review. A
 * draft still being written, one whose generation failed, and one already approved or live have
 * nothing left to decide here.
 */
export const REVIEWABLE_STATUSES: readonly ContentOptimizationStatus[] = [
  ContentOptimizationStatus.SystemReview,
  ContentOptimizationStatus.UserReview,
];

/**
 * What a screen says about a draft the site wrote: that it awaits the review, which SiteDraftReviewNotice says in
 * the words of the screen showing it, or what became of it once it left the review, and whether it went live.
 */
export type ISiteDraftNotice = { inReview: true; live: false } | { inReview: false; live: boolean; message: string };

/**
 * What a screen says about a draft the site wrote: that it awaits the review, that it is live once the site
 * reported it published, or why it was not published. Nothing for a draft Sync Core generated, and nothing for a
 * site's draft in any other state; the gaps speak for the page then.
 */
export function toSiteDraftNotice(optimization: ClientContentOptimizationEntity | undefined): ISiteDraftNotice | undefined {
  if (!optimization?.externalRevisionId) {
    return undefined;
  }

  if (REVIEWABLE_STATUSES.includes(optimization.status)) {
    return { inReview: true, live: false };
  }

  if (optimization.status === ContentOptimizationStatus.Applied) {
    return { message: SITE_DRAFT_LIVE_COPY, inReview: false, live: true };
  }

  if (optimization.status === ContentOptimizationStatus.Aborted && optimization.statusReason) {
    return { message: optimization.statusReason, inReview: false, live: false };
  }

  return undefined;
}
